import styled from '@emotion/styled';
import { Input, Select, SelectProps, Tooltip } from 'antd';
import { useTranslate } from '@tolgee/react';
import FormField from 'components/FormField';
import { FV } from '../TemplateForm/share';
import useSmartField from 'hooks/useSmartField';
import { What } from 'views/Aide/Scenario/PolicyTable';
import { regionPhoneMap } from '../share';
import { useMemo } from 'react';

function RegionPhoneSelect(props: SelectProps<string>) {
  const { t } = useTranslate();

  const options = useMemo(() => {
    return Object.keys(regionPhoneMap).map((key) => {
      return {
        value: `+${regionPhoneMap[key]}`,
        label: `+${regionPhoneMap[key]}`,
      };
    });
  }, []);

  return (
    <Select
      showSearch
      placeholder={t('department.请选择x', { x: t('国家/地区') })}
      options={options}
      optionFilterProp="label"
      {...props}
    />
  );
}

interface ButtonProps {
  region: Aide.WhatsAppRegion;
  index: number;
}

function ButtonText(props: ButtonProps) {
  const { region, index } = props;

  const { t } = useTranslate();

  return (
    <FormField<Required<Required<FV['regionTemplateMap']>['CN']>['buttons'][0]['text']>
      name={`regionTemplateMap.${region}.buttons.${index}.text`}
      validate={(value) =>
        value && value.trim() ? undefined : t('department.请输入x', { x: t('按钮文本') })
      }
    >
      {({ field }) => {
        return (
          <Input
            placeholder={t('department.请输入x', { x: t('按钮文本') })}
            {...field}
            showCount
            maxLength={25}
          />
        );
      }}
    </FormField>
  );
}

const ButtonWrapper = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 12px 0;
  overflow: hidden;
  background: var(--fett-erp-card-bg);
  > dl {
    flex: 1 0 0;
    margin-bottom: 0;
    overflow: hidden;
  }
`;

export function QuickReplyButton(props: ButtonProps) {
  const { region, index } = props;

  const { t } = useTranslate();

  return (
    <ButtonWrapper>
      <dl>
        <dd>{t('按钮文本')}</dd>
        <dt>
          <ButtonText region={region} index={index} />
        </dt>
      </dl>
    </ButtonWrapper>
  );
}

const UrlWrapper = styled.div`
  display: flex;
  align-items: center;
  > span {
    margin: 2px;
    font-size: 12px;
    color: var(--fett-text-secondary);
  }
`;

export const urlConcept = '{{1}}';

export const urlConceptReg = /\{\{1\}\}$/;

interface ActivityButtonProps extends ButtonProps {
  optionTypeDisabled?: boolean;
}

export function ActivityButton(props: ActivityButtonProps) {
  const { region, index, optionTypeDisabled = false } = props;

  const { t } = useTranslate();

  const {
    field: { value: buttonValue, onChange: onButtonChange },
  } = useSmartField<Aide.WhatsAppActivityButton>(`regionTemplateMap.${region}.buttons.${index}`);

  return (
    <ButtonWrapper>
      <dl>
        <dd>{t('操作类型')}</dd>
        <dt>
          <FormField<Aide.WhatsAppActivityButton['optionType']>
            name={`regionTemplateMap.${region}.buttons.${index}.optionType`}
          >
            {({ field: { onChange, ...others } }) => {
              return (
                <Select
                  disabled={optionTypeDisabled}
                  {...others}
                  options={[
                    {
                      value: 'PHONE_NUM',
                      label: t('拨打电话'),
                    },
                    {
                      value: 'URL',
                      label: t('跳转链接'),
                    },
                  ]}
                  onChange={(optionType) => {
                    if (optionType === 'PHONE_NUM') {
                      onButtonChange({
                        type: 'ACTIVITY',
                        optionType,
                        text: '',
                        region: 'cn',
                        phone: '',
                      });
                    }

                    if (optionType === 'URL') {
                      onButtonChange({
                        type: 'ACTIVITY',
                        optionType,
                        text: '',
                        urlType: 'STATIC',
                        url: '',
                      });
                    }
                  }}
                />
              );
            }}
          </FormField>
        </dt>
      </dl>
      <dl>
        <dd>{t('按钮文本')}</dd>
        <dt>
          <ButtonText region={region} index={index} />
        </dt>
      </dl>
      {buttonValue.optionType === 'PHONE_NUM' && (
        <dl>
          <dd>{t('国家/地区')}</dd>
          <dt>
            <FormField<Aide.WhatsAppActivityPhoneNumButton['region']>
              name={`regionTemplateMap.${region}.buttons.${index}.region`}
              validate={(value) =>
                value ? undefined : t('department.请选择x', { x: t('国家/地区') })
              }
            >
              {({ field }) => {
                return <RegionPhoneSelect {...field} />;
              }}
            </FormField>
          </dt>
        </dl>
      )}
      {buttonValue.optionType === 'PHONE_NUM' && (
        <dl>
          <dd>{t('trade.电话号码')}</dd>
          <dt>
            <FormField<Aide.WhatsAppActivityPhoneNumButton['phone']>
              name={`regionTemplateMap.${region}.buttons.${index}.phone`}
              validate={(value) =>
                value && value.trim()
                  ? undefined
                  : t('department.请输入x', { x: t('trade.电话号码') })
              }
            >
              {({ field }) => {
                return (
                  <Input
                    placeholder={t('department.请输入x', { x: t('trade.电话号码') })}
                    {...field}
                    showCount
                    maxLength={25}
                  />
                );
              }}
            </FormField>
          </dt>
        </dl>
      )}
      {buttonValue.optionType === 'URL' && (
        <dl>
          <dd>{t('网址类型')}</dd>
          <dt>
            <FormField<Aide.WhatsAppActivityUrlButton['urlType']>
              name={`regionTemplateMap.${region}.buttons.${index}.urlType`}
              validate={(value) =>
                value ? undefined : t('department.请选择x', { x: t('网址类型') })
              }
            >
              {({ field: { value, onChange, ...others } }) => {
                return (
                  <Select
                    placeholder={t('department.请选择x', { x: t('网址类型') })}
                    value={value}
                    onChange={(urlType) => {
                      onButtonChange({
                        ...buttonValue,
                        urlType,
                        url: urlType === 'DYNAMIC' ? urlConcept : '',
                      });
                    }}
                    {...others}
                    options={[
                      {
                        value: 'STATIC',
                        label: t('静态'),
                      },
                      {
                        value: 'DYNAMIC',
                        label: t('动态'),
                      },
                    ]}
                  />
                );
              }}
            </FormField>
          </dt>
        </dl>
      )}
      {buttonValue.optionType === 'URL' && (
        <dl>
          <dd>{t('WhatsApp.网址')}</dd>
          <dt>
            <FormField<Aide.WhatsAppActivityUrlButton['url']>
              name={`regionTemplateMap.${region}.buttons.${index}.url`}
              validate={(value) => {
                let url = value || '';

                if (buttonValue.urlType === 'DYNAMIC') {
                  url = value.replace(urlConceptReg, '');
                }

                return url && url.trim()
                  ? undefined
                  : t('department.请输入x', { x: t('WhatsApp.网址') });
              }}
            >
              {({ field }) => {
                if (buttonValue.urlType === 'STATIC') {
                  return (
                    <Input
                      placeholder={t('department.请输入x', { x: t('WhatsApp.网址') })}
                      {...field}
                    />
                  );
                }

                const { value, onChange, ...others } = field;

                return (
                  <UrlWrapper>
                    <Input
                      placeholder={t('department.请输入x', { x: t('WhatsApp.网址') })}
                      value={value.replace(urlConceptReg, '')}
                      onChange={({ target: { value } }) =>
                        onButtonChange({
                          ...buttonValue,
                          url: `${value}${urlConcept}`,
                          example: `${value}123`,
                        })
                      }
                      {...others}
                    />
                    <span>{urlConcept}</span>
                    <Tooltip
                      title={
                        <div>
                          {t(
                            '添加变量即可在使用模板时赋值不同的网址。您只能添加一个变量到网址结尾处。',
                          )}
                          <a
                            href="https://www.facebook.com/business/help/448422200528701"
                            target="__blank"
                          >
                            {t('详细了解')}
                          </a>
                        </div>
                      }
                    >
                      <What />
                    </Tooltip>
                  </UrlWrapper>
                );
              }}
            </FormField>
          </dt>
        </dl>
      )}
    </ButtonWrapper>
  );
}
